import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import toast from 'react-hot-toast';
import { useCreateShareLink, useDeleteShareLink, useShareLinks } from '../hooks/useShareLinks';
import { usePromotableEvents } from '../hooks/useEvents';
import { getRegisterUrlForEvent, getShareableShortUrl } from '../utils/sharingLinkUrl';
import * as adminService from '../services/adminService';

const { FiLink, FiCopy, FiExternalLink, FiCalendar, FiUsers, FiShare2, FiTrash2 } = FiIcons;

const IDENTIFIER_PATTERN = /^[A-Za-z0-9_-]+$/;

const SharingLinks = () => {
  const { user, session, role } = useAuth();
  // Use session.user.id (auth.uid()) for RLS; service also enforces auth.uid() for insert agent_id
  const authUserId = session?.user?.id ?? user?.id;
  const promotableEventsQuery = usePromotableEvents(
    { role, userId: authUserId },
    { enabled: Boolean(authUserId || role === 'admin'), realtime: true }
  );
  const promotableEvents = promotableEventsQuery.data ?? [];

  const isAdmin = role === 'admin';
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    setAgentsLoading(true);
    adminService.listUsers({ role: 'admin', userId: authUserId }).then(({ data, error }) => {
      if (cancelled) return;
      setAgentsLoading(false);
      if (error) {
        toast.error(error.message ?? 'Failed to load agents');
        return;
      }
      const agentUsers = (data ?? []).filter((u) => u.role === 'agent');
      setAgents(agentUsers);
    });
    return () => { cancelled = true; };
  }, [isAdmin, authUserId]);

  const [copiedId, setCopiedId] = useState(null);
  const [formData, setFormData] = useState({
    eventId: '',
    agentId: '',
    customIdentifier: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [needsRetry, setNeedsRetry] = useState(false);
  const [linkToDelete, setLinkToDelete] = useState(null);

  const shareLinksQuery = useShareLinks(
    { role, userId: authUserId, eventIds: promotableEvents.map(event => event.id) },
    { enabled: Boolean(authUserId || role === 'admin'), realtime: true }
  );
  const shareLinks = shareLinksQuery.data ?? [];
  const createShareLink = useCreateShareLink({ role, userId: authUserId });
  const deleteShareLink = useDeleteShareLink({ role, userId: authUserId });

  useEffect(() => {
    if (!formData.eventId && promotableEvents.length > 0) {
      setFormData((prev) => ({ ...prev, eventId: promotableEvents[0].id }));
    }
  }, [formData.eventId, promotableEvents]);

  useEffect(() => {
    if (!createShareLink.error) return;
    const message = createShareLink.error?.message || 'Unable to create the invitation link.';
    toast.error(message);
  }, [createShareLink.error]);

  const copyToClipboard = async (text, eventId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(eventId);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const getEventLink = (eventId) => {
    const event = promotableEvents.find((e) => e.id === eventId);
    return getRegisterUrlForEvent(event ? { id: event.id, custom_domain: event.custom_domain } : { id: eventId });
  };

  const getEventTitle = (eventId) => {
    return promotableEvents.find((event) => event.id === eventId)?.title || 'Unknown event';
  };

  const validateIdentifier = (identifier, eventId) => {
    const trimmed = (identifier || '').trim().toLowerCase();

    if (!trimmed) {
      return 'Custom identifier is required.';
    }

    if (trimmed.length < 3 || trimmed.length > 50) {
      return 'Identifier must be between 3 and 50 characters.';
    }

    if (!IDENTIFIER_PATTERN.test(trimmed)) {
      return 'Use letters, numbers, underscores, or hyphens only.';
    }

    const duplicateExists = shareLinks.some((link) => {
      if (link.event_id !== eventId) return false;
      if (!link.slug) return false;
      return link.slug.toLowerCase() === trimmed;
    });

    if (duplicateExists) {
      return 'Identifier already exists for this event.';
    }

    return null;
  };

  const handleFieldChange = (field) => (event) => {
    let value = event.target.value;
    if (field === 'customIdentifier') {
      value = value.toLowerCase();
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: undefined }));
    setNeedsRetry(false);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const trimmedIdentifier = formData.customIdentifier.trim().toLowerCase();
    const nextErrors = {};

    if (!formData.eventId) {
      nextErrors.eventId = 'Select an event to create an invitation link.';
    }

    if (isAdmin && !formData.agentId) {
      nextErrors.agentId = 'Select an agent to create the link on behalf of.';
    }

    const identifierError = validateIdentifier(trimmedIdentifier, formData.eventId);
    if (identifierError) {
      nextErrors.customIdentifier = identifierError;
    }

    if (Object.keys(nextErrors).length > 0) {
      setFormErrors(nextErrors);
      setNeedsRetry(Boolean(identifierError));
      return;
    }

    const mutationPayload = {
      event_id: formData.eventId,
      slug: trimmedIdentifier
    };
    if (isAdmin) {
      mutationPayload.agent_id = formData.agentId;
    }

    createShareLink.mutate(
      mutationPayload,
      {
        onSuccess: () => {
          setFormData((prev) => ({ ...prev, customIdentifier: '', ...(isAdmin ? { agentId: '' } : {}) }));
          setFormErrors({});
          setNeedsRetry(false);
          toast.success('Invitation link created.');
        },
        onError: () => {
          setNeedsRetry(true);
        }
      }
    );
  };

  const handleDeleteConfirm = () => {
    if (!linkToDelete) return;
    deleteShareLink.mutate(linkToDelete.id, {
      onSuccess: () => {
        toast.success('Invitation link deleted.');
        setLinkToDelete(null);
      },
      onError: (error) => {
        const message = error?.message || 'Unable to delete the invitation link.';
        toast.error(message);
      }
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
          Invitation Links
        </h1>
        <p className="text-slate-600 mt-2">Share your event registration links with potential attendees</p>
      </div>

      {promotableEventsQuery.isLoading ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-sm border border-slate-200 p-12 text-center"
        >
          <p className="text-slate-600">Loading promotable events...</p>
        </motion.div>
      ) : promotableEvents.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-sm border border-slate-200 p-12 text-center"
        >
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <SafeIcon icon={FiLink} className="w-8 h-8 text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No Events Yet</h3>
          <p className="text-slate-600 mb-4">
            There are no promotable events (published/public) you can create invitation links for yet.
          </p>
        </motion.div>
      ) : (
        <div className="space-y-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6"
          >
            <div className="flex items-center space-x-2 mb-6">
              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                <SafeIcon icon={FiShare2} className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Create an invitation link</h2>
                <p className="text-sm text-slate-600">Generate a custom identifier for an event link.</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className={`grid gap-4 ${isAdmin ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2" htmlFor="share-event">
                    Event
                  </label>
                  <select
                    id="share-event"
                    value={formData.eventId}
                    onChange={handleFieldChange('eventId')}
                    className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300"
                  >
                    <option value="" disabled>Select an event</option>
                    {promotableEvents.map((event) => (
                      <option key={event.id} value={event.id}>
                        {event.title}
                      </option>
                    ))}
                  </select>
                  {formErrors.eventId && (
                    <p className="mt-2 text-xs text-rose-600">{formErrors.eventId}</p>
                  )}
                </div>

                {isAdmin && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2" htmlFor="share-agent">
                      Agent
                    </label>
                    <select
                      id="share-agent"
                      value={formData.agentId}
                      onChange={handleFieldChange('agentId')}
                      disabled={agentsLoading}
                      className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300"
                    >
                      <option value="" disabled>
                        {agentsLoading ? 'Loading agents...' : 'Select an agent'}
                      </option>
                      {agents.map((agent) => (
                        <option key={agent.id} value={agent.id}>
                          {agent.full_name || agent.email || agent.id}
                        </option>
                      ))}
                    </select>
                    {formErrors.agentId && (
                      <p className="mt-2 text-xs text-rose-600">{formErrors.agentId}</p>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2" htmlFor="custom-identifier">
                    Custom Identifier
                  </label>
                  <input
                    id="custom-identifier"
                    type="text"
                    value={formData.customIdentifier}
                    onChange={handleFieldChange('customIdentifier')}
                    placeholder="e.g. yourname or summer-open-house"
                    autoComplete="off"
                    className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300"
                  />
                  <p className="mt-2 text-xs text-slate-500">
                    Use 3–50 characters: letters, numbers, underscores, or hyphens. Stored in lowercase.
                  </p>
                  {formData.customIdentifier.trim() && (
                    <p className="mt-1.5 text-xs text-slate-600 font-mono">
                      URL will be: {(() => {
                        const ev = promotableEvents.find((e) => e.id === formData.eventId);
                        const path = `/${ev?.event_slug ?? 'event-slug'}/${formData.customIdentifier.trim().toLowerCase()}`;
                        return ev ? getShareableShortUrl(ev, { slug: formData.customIdentifier.trim().toLowerCase() }) : path;
                      })()}
                    </p>
                  )}
                  {formErrors.customIdentifier && (
                    <p className="mt-2 text-xs text-rose-600">{formErrors.customIdentifier}</p>
                  )}
                  {needsRetry && !formErrors.customIdentifier && (
                    <p className="mt-2 text-xs text-amber-600">
                      Update the identifier to retry.
                    </p>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="submit"
                  disabled={createShareLink.isPending || needsRetry}
                  className={`px-5 py-3 rounded-lg font-medium transition-all flex items-center space-x-2 ${
                    createShareLink.isPending || needsRetry
                      ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                      : 'bg-slate-900 text-white hover:bg-slate-800'
                  }`}
                >
                  <SafeIcon icon={FiLink} className="w-4 h-4" />
                  <span>{createShareLink.isPending ? 'Creating...' : 'Create Link'}</span>
                </button>
                {shareLinksQuery.isFetching && (
                  <span className="text-xs text-slate-500">Refreshing links...</span>
                )}
              </div>
            </form>
          </motion.div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">Existing invitation links</h2>
              {shareLinksQuery.isLoading && (
                <span className="text-xs text-slate-500">Loading links...</span>
              )}
            </div>

            {shareLinks.length === 0 ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl shadow-sm border border-slate-200 p-10 text-center"
              >
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiLink} className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">No invitation links yet</h3>
                <p className="text-slate-600">Create a custom identifier to start tracking shared links.</p>
              </motion.div>
            ) : (
              <div className="grid gap-4">
                {shareLinks.map((link) => {
                  const event = promotableEvents.find((e) => e.id === link.event_id);
                  const destinationUrl = link.destination_url || getEventLink(link.event_id);
                  const shareableShortUrl = event && link.slug
                    ? getShareableShortUrl(event, link)
                    : destinationUrl;

                  return (
                    <div
                      key={link.id}
                      className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <div>
                          <p className="text-xs uppercase tracking-wide text-slate-500">Event</p>
                          <h3 className="text-lg font-semibold text-slate-900">
                            {getEventTitle(link.event_id)}
                          </h3>
                          <div className="flex items-center space-x-3 mt-2 text-sm text-slate-600">
                            <span className="flex items-center space-x-1">
                              <SafeIcon icon={FiCalendar} className="w-4 h-4" />
                              <span>{link.event_id}</span>
                            </span>
                            {link.created_at && (
                              <span className="flex items-center space-x-1">
                                <SafeIcon icon={FiUsers} className="w-4 h-4" />
                                <span>Created {new Date(link.created_at).toLocaleDateString()}</span>
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xs uppercase tracking-wide text-slate-500">Identifier</p>
                          <p className="text-lg font-semibold text-slate-900">{link.slug || '—'}</p>
                        </div>
                      </div>

                      <div className="mt-5 space-y-3">
                        {shareableShortUrl !== destinationUrl && (
                          <div>
                            <label className="block text-xs font-medium text-slate-500 mb-1">
                              Shareable link (short)
                            </label>
                            <div className="flex items-center space-x-2">
                              <div className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-4 py-2 text-sm text-slate-600 font-mono truncate">
                                {shareableShortUrl}
                              </div>
                              <button
                                type="button"
                                onClick={() => copyToClipboard(shareableShortUrl, `short-${link.id}`)}
                                className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center space-x-2 ${
                                  copiedId === `short-${link.id}`
                                    ? 'bg-emerald-100 text-emerald-700'
                                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                }`}
                              >
                                <SafeIcon icon={FiCopy} className="w-4 h-4" />
                                <span>{copiedId === `short-${link.id}` ? 'Copied!' : 'Copy'}</span>
                              </button>
                            </div>
                          </div>
                        )}

                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Destination URL
                          </label>
                          <div className="flex items-center space-x-2">
                            <div className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-600 font-mono truncate">
                              {destinationUrl}
                            </div>
                            <button
                              type="button"
                              onClick={() => copyToClipboard(destinationUrl, link.id)}
                              className={`px-4 py-3 rounded-lg font-medium transition-all flex items-center space-x-2 ${
                                copiedId === link.id
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                              }`}
                            >
                              <SafeIcon icon={FiCopy} className="w-4 h-4" />
                              <span>{copiedId === link.id ? 'Copied!' : 'Copy'}</span>
                            </button>
                            <a
                              href={destinationUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-4 py-3 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-all"
                            >
                              <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
                            </a>
                            <button
                              type="button"
                              onClick={() => setLinkToDelete(link)}
                              className="px-4 py-3 bg-rose-50 text-rose-700 rounded-lg hover:bg-rose-100 transition-all"
                            >
                              <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {linkToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-slate-900">Delete link?</h3>
            <p className="mt-2 text-sm text-slate-600">
              This will stop tracking future registrations via this link.
            </p>
            <div className="mt-6 flex flex-wrap justify-end gap-3">
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800"
                onClick={() => setLinkToDelete(null)}
                disabled={deleteShareLink.isPending}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 text-sm font-semibold text-white bg-rose-600 rounded-lg hover:bg-rose-700 disabled:opacity-60"
                onClick={handleDeleteConfirm}
                disabled={deleteShareLink.isPending}
              >
                {deleteShareLink.isPending ? 'Deleting...' : 'Delete link'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SharingLinks;
